import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { useAppContext } from '@/hooks'
import { useUpdateBranchMutation } from '@/rtkApi'
import { isApproverCount } from '@/utils/approverMinimum'
import {
	getApiErrorDescription,
	logErrorAndNotifyWithToast,
} from '@/utils/errorHandling'

import { branchNameError } from '../branchDefinition'
import { toBranchUpdateDto } from '../mapBranch'
import { BranchRecord } from '../types'

type UseEditBranchDialogArgs = {
	branch: BranchRecord | null
	branches: BranchRecord[]
	isOpen: boolean
	minimumNumberOfApprovers: number
	onClose: () => void
}

export const useEditBranchDialog = ({
	branch,
	branches,
	isOpen,
	minimumNumberOfApprovers,
	onClose,
}: UseEditBranchDialogArgs) => {
	const { t } = useAppContext()
	const [updateBranch, { isLoading: isSaving }] = useUpdateBranchMutation()
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [requiredApprovals, setRequiredApprovalsState] = useState(
		minimumNumberOfApprovers,
	)
	const approvalsTouched = useRef(false)
	const minimumRef = useRef(minimumNumberOfApprovers)
	minimumRef.current = minimumNumberOfApprovers
	const nameLocked = Boolean(branch && !branch.sourceBranchId)

	useEffect(() => {
		if (!isOpen || !branch) {
			approvalsTouched.current = false
			return
		}
		approvalsTouched.current = false
		setName(branch.name)
		setDescription(branch.description)
		setRequiredApprovalsState(
			branch.requiredApprovals ?? minimumRef.current,
		)
	}, [branch, isOpen])

	useEffect(() => {
		if (!isOpen || !branch || approvalsTouched.current) return
		if (branch.requiredApprovals != null) return
		setRequiredApprovalsState(minimumNumberOfApprovers)
	}, [branch, isOpen, minimumNumberOfApprovers])

	const setRequiredApprovals = useCallback((value: number) => {
		approvalsTouched.current = true
		setRequiredApprovalsState(value)
	}, [])

	const nameError = nameLocked || !branch
		? null
		: branchNameError(name, branches, branch.id)
	const approvalsValid = isApproverCount(
		requiredApprovals,
		minimumNumberOfApprovers,
	)

	const handleClose = useCallback(() => {
		approvalsTouched.current = false
		onClose()
	}, [onClose])

	const handleSave = useCallback(async () => {
		if (!branch || nameError || !approvalsValid) return
		try {
			await updateBranch({
				branchId: Number(branch.id),
				branchDto: toBranchUpdateDto(branch, {
					description: description.trim() || undefined,
					name: nameLocked ? branch.name : name.trim(),
					requiredApprovals,
				}),
			}).unwrap()
			toast(t('BRANCHES_DEFINITION_SAVED'), { type: 'success' })
			handleClose()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'EditBranchDialog.handleSave' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'BRANCHES_UPDATE_FAILED',
				t,
			})
		}
	}, [
		approvalsValid,
		branch,
		description,
		handleClose,
		name,
		nameError,
		nameLocked,
		requiredApprovals,
		t,
		updateBranch,
	])

	return {
		approvalsValid,
		description,
		handleClose,
		handleSave,
		isSaving,
		minimumNumberOfApprovers,
		name,
		nameError,
		nameLocked,
		requiredApprovals,
		setDescription,
		setName,
		setRequiredApprovals,
	}
}
